import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { modalRoutes } from 'constants/routes';
import { useModalHistory } from 'context/ModalRouter';
import { createSearchFromParams } from 'utils/url';
import { popupConfirm } from 'utils/popup';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';

export type PostExperience = 'new' | 'legacy';

type Params = {
  current: PostExperience;
  postId?: number | null;
  pageId?: number | null;
  hasUnsavedChanges?: () => boolean;
  saveDraft?: () => Promise<number | undefined>;
};

/**
 * Which composer renders is decided by the URL hash alone — `#post-workspace`
 * and `#post` are two independent modal routes. Flipping the store only changes
 * where future links point, so switching has to swap the hash as well and
 * translate the query parameters between the two routes.
 *
 * Legacy always needs `compose=true`; without it the post opens in the
 * read-only overview instead of the composer.
 */
export function usePostExperienceSwitch({
  current,
  postId,
  pageId,
  hasUnsavedChanges,
  saveDraft,
}: Params) {
  const { t } = useTranslation();
  const modalHistory = useModalHistory();
  const setNewPostExperience = usePostsLayoutState(
    (state) => state.setNewPostExperience,
  );

  const target: PostExperience = current === 'new' ? 'legacy' : 'new';

  const confirmSwitch = useCallback(async () => {
    if (!hasUnsavedChanges?.()) {
      return true;
    }

    const result = await popupConfirm({
      title: t('postExperienceSwitch.confirmTitle', {
        defaultValue: 'Switch post experience?',
      }),
      text: t('postExperienceSwitch.confirmUnsavedChanges', {
        defaultValue:
          'Your unsaved changes will be lost when you switch. Do you want to continue?',
      }),
      confirmButtonText: t('continue', { defaultValue: 'Continue' }),
      customClass: {
        popup: '!tw-w-[400px]',
      },
    });

    return result.isConfirmed;
  }, [hasUnsavedChanges, t]);

  const openInNewExperience = useCallback(
    (id: number | null | undefined) => {
      setNewPostExperience(true);
      modalHistory.replaceLast({
        hash: modalRoutes.POST_DETAIL_NEW,
        search: createSearchFromParams({
          id,
          pageId,
          /**
           * The legacy composer blocks any navigation that drops `compose`,
           * even when pristine. We already asked, so bypass the second prompt.
           */
          submitSuccessful: true,
        }),
      });
    },
    [modalHistory, pageId, setNewPostExperience],
  );

  const switchExperience = useCallback(async () => {
    /**
     * The legacy create flow keeps a new post in memory only, so it is saved
     * as a draft first and then opened on the other side with its content.
     */
    if (current === 'legacy' && !postId && saveDraft) {
      const savedPostId = await saveDraft();

      if (savedPostId) {
        openInNewExperience(savedPostId);
      }

      return;
    }

    if (!(await confirmSwitch())) {
      return;
    }

    if (target === 'new') {
      openInNewExperience(postId);
      return;
    }

    setNewPostExperience(false);
    modalHistory.replaceLast({
      hash: modalRoutes.POST_DETAIL,
      search: createSearchFromParams({
        id: postId,
        profileIds: postId ? undefined : pageId,
        compose: true,
      }),
    });
  }, [
    current,
    postId,
    saveDraft,
    confirmSwitch,
    openInNewExperience,
    setNewPostExperience,
    modalHistory,
    pageId,
    target,
  ]);

  return { switchExperience, target };
}
